import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import {
  Send, Bot, User, Loader2, Sparkles, Plus, TrendingUp, Calculator, Bell,
  ShieldCheck, Zap, ChevronRight, ArrowUpRight, History, PiggyBank,
} from 'lucide-react';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { CategoryTabs } from '../components/chat/CategoryTabs';
import { SessionList } from '../components/chat/SessionList';
import { MessageContent } from '../components/chat/MessageContent';
import { getCategory } from '../components/chat/chatCategories';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STOCK_RE = /\b(CRDB|NMB|TBL|TCC|TWIGA|VODA|NICOL|TCCL|DCB|MKCB|MCB|KCB|JHL|EABL|SWIS|TPCC|PAL|TOL|USL|YETU|SWALA|TATEPA|NMG|JATU|MUCOBA|AFRIPRISE|DSE)\b/i;

export default function Chat() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user, token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [category, setCategory] = useState(() => (getCategory(searchParams.get('category')).id));
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState(() => (searchParams.get('send') === '1' ? '' : searchParams.get('q') || ''));
  const autoAsk = useRef(searchParams.get('send') === '1' ? searchParams.get('q') : null);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const scrollRef = useRef(null);
  const lastMsgRef = useRef(null);
  const inputRef = useRef(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const cat = getCategory(category);

  const fetchSessions = useCallback(async (cid) => {
    try {
      const res = await axios.get(`${API}/chat/sessions?category=${cid}`, { headers: { Authorization: `Bearer ${token}` } });
      setSessions(res.data.sessions || []);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    }
  }, [token]);

  const welcomeFor = useCallback((cid) => {
    const name = user?.name?.split(' ')[0] || '';
    const c = getCategory(cid);
    const label = language === 'sw' ? c.sw : c.en;
    if (cid === 'general') {
      return language === 'sw'
        ? `Habari ${name}! 👋 Mimi ni mshauri wako wa uwekezaji wa AI. Ninatumia bei za DSE za leo, riba za BoT, UTT AMIS, ETF, FPRF na portfolio yako.\n\nChagua mada hapo juu au uliza chochote — nitaeleza **nini, kwa nini na jinsi gani**.`
        : `Hi ${name}! 👋 I'm your AI investment coach. I use today's DSE prices, BoT yields, UTT AMIS, ETFs, FPRF and your own portfolio.\n\nPick a topic above or ask anything — I'll explain the **what, why and how**.`;
    }
    return language === 'sw'
      ? `**${label}** — uliza swali lolote kuhusu mada hii. Unaweza kuuliza "kwa nini?" au "vipi?" baada ya kila jibu.`
      : `**${label}** — ask me anything on this topic. Follow up with "why?" or "how?" after any answer.`;
  }, [language, user]);

  const resetChat = useCallback((cid) => {
    setSessionId(null);
    setMessages([{ role: 'assistant', content: welcomeFor(cid), type: 'welcome' }]);
  }, [welcomeFor]);

  // Reset once per category change (guarded so StrictMode's double effect can't wipe an auto-asked question).
  // Deep links like /chat?q=...&send=1 (e.g. from Intelligence) ask the question immediately, once.
  const initialisedFor = useRef(null);
  useEffect(() => {
    if (initialisedFor.current === category) return;
    initialisedFor.current = category;
    resetChat(category);
    fetchSessions(category);
    if (autoAsk.current) {
      const q = autoAsk.current;
      autoAsk.current = null;
      setSearchParams({}, { replace: true });
      sendMessage(null, q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  // Language switch only refreshes the welcome text; it never wipes an active thread.
  useEffect(() => {
    setMessages((prev) => (prev.length === 1 && prev[0].type === 'welcome'
      ? [{ ...prev[0], content: welcomeFor(category) }] : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  // Scroll inside the chat pane only; new assistant replies are shown from their first line.
  useEffect(() => {
    const vp = scrollRef.current?.querySelector('[data-radix-scroll-area-viewport]');
    if (!vp) return;
    const last = messages[messages.length - 1];
    if (!loading && last?.role === 'assistant' && lastMsgRef.current && messages.length > 1) {
      vp.scrollTop += lastMsgRef.current.getBoundingClientRect().top - vp.getBoundingClientRect().top - 16;
    } else {
      vp.scrollTop = vp.scrollHeight;
    }
  }, [messages, loading]);

  const loadSession = async (s) => {
    try {
      const res = await axios.get(`${API}/chat/history/${s.session_id}`, auth);
      setMessages(res.data.history || []);
      setSessionId(s.session_id);
      setHistoryOpen(false);
    } catch (error) {
      toast.error(t('Failed to load chat history', 'Imeshindwa kupakia historia ya mazungumzo'));
    }
  };

  const sendMessage = async (e, customMessage = null) => {
    e?.preventDefault();
    const text = (customMessage || input).trim();
    if (!text || loading) return;
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setInput('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, { message: text, session_id: sessionId, language, category }, auth);
      setMessages((prev) => [...prev, { role: 'assistant', content: res.data.response }]);
      setSessionId(res.data.session_id);
      fetchSessions(category);
    } catch (error) {
      const msg = error.response?.status === 429
        ? t('You have reached the hourly question limit. Please try again later.', 'Umefikia kikomo cha maswali kwa saa. Jaribu tena baadaye.')
        : t('Failed to get response. Please try again.', 'Imeshindwa kupata jibu. Tafadhali jaribu tena.');
      toast.error(msg);
      setMessages((prev) => prev.slice(0, -1));
      setInput(text);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const parseMessageActions = (content) => {
    const actions = [];
    const lower = content.toLowerCase();
    const stock = content.match(STOCK_RE);
    if (stock && stock[1].toUpperCase() !== 'DSE') {
      const sym = stock[1].toUpperCase();
      actions.push({ label: t(`View ${sym}`, `Ona ${sym}`), action: () => navigate(`/stocks/${sym}`), icon: TrendingUp });
      actions.push({ label: t('Set Alert', 'Weka Arifa'), action: () => navigate('/alerts'), icon: Bell });
    }
    if (lower.includes('simulator') || lower.includes('calculat') || lower.includes('hesabu')) {
      actions.push({ label: t('Open Simulator', 'Fungua Kihesabu'), action: () => navigate('/simulator'), icon: Calculator });
    }
    if (lower.includes('risk profile') || lower.includes('wasifu wa hatari')) {
      actions.push({ label: t('Take Quiz', 'Fanya Mtihani'), action: () => navigate('/quiz'), icon: ShieldCheck });
    }
    return actions;
  };

  const starters = cat.starters[language === 'sw' ? 'sw' : 'en'];
  const isFresh = messages.length <= 1 && !sessionId;
  const sessionList = (
    <SessionList
      sessions={sessions}
      activeId={sessionId}
      onSelect={loadSession}
      language={language}
      emptyLabel={t('No saved chats in this topic yet', 'Hakuna mazungumzo yaliyohifadhiwa kwenye mada hii')}
    />
  );

  return (
    <Layout hideNav>
      <div className="flex h-[calc(100dvh-4rem-1px)]">
        <aside className="hidden lg:flex lg:w-72 flex-col border-r border-border/50 bg-gradient-to-b from-background to-muted/20">
          <div className="p-4 border-b border-border/50">
            <Button onClick={() => resetChat(category)} className="w-full rounded-xl shadow-lg shadow-primary/20" data-testid="new-chat-btn">
              <Zap className="w-4 h-4 mr-2" />
              {t('New Conversation', 'Mazungumzo Mapya')}
            </Button>
          </div>
          <div className="px-4 py-3 border-b border-border/50 flex items-center gap-2 text-xs text-muted-foreground">
            <Sparkles className="w-3 h-3 text-primary" />
            <span>{t('Same knowledge as our WhatsApp coach + live data', 'Maarifa sawa na WhatsApp + data ya moja kwa moja')}</span>
          </div>
          <ScrollArea className="flex-1 p-3">
            <p className="text-xs text-muted-foreground uppercase tracking-wider px-2 mb-2" data-testid="chat-history-heading">
              {t(`${cat.en} history`, `Historia ya ${cat.sw}`)}
            </p>
            {sessionList}
          </ScrollArea>
        </aside>

        <div className="flex-1 flex flex-col min-w-0 bg-gradient-to-b from-background via-background to-muted/10">
          <div className="p-4 border-b border-border/50 bg-background/80 backdrop-blur-sm flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center shadow-lg shadow-primary/20">
                  <Sparkles className="w-6 h-6 text-primary-foreground" />
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-background" />
              </div>
              <div>
                <h1 className="font-bold text-lg text-foreground">{t('AI Investment Coach', 'Mshauri wa Uwekezaji wa AI')}</h1>
                <span className="text-xs text-muted-foreground" data-testid="chat-active-category">
                  {t('Topic', 'Mada')}: {language === 'sw' ? cat.sw : cat.en}
                </span>
              </div>
            </div>
            <div className="flex gap-2 lg:hidden">
              <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm" className="rounded-xl" data-testid="chat-history-mobile-btn">
                    <History className="w-4 h-4" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-80 p-3">
                  <SheetHeader><SheetTitle>{t(`${cat.en} history`, `Historia ya ${cat.sw}`)}</SheetTitle></SheetHeader>
                  <div className="mt-4 overflow-y-auto max-h-[80vh]">{sessionList}</div>
                </SheetContent>
              </Sheet>
              <Button variant="outline" size="sm" onClick={() => resetChat(category)} className="rounded-xl" data-testid="new-chat-mobile-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <CategoryTabs active={category} onChange={setCategory} language={language} />
          {category === 'cis' && (
            <Link to="/utt-amis?tab=open-account" data-testid="chat-utt-open-account-cta"
              className="mx-4 mt-3 flex items-center gap-3 rounded-2xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm transition-colors duration-200 hover:bg-primary/10">
              <PiggyBank className="h-5 w-5 text-primary" />
              <span className="flex-1">{t('Ready to invest? Open & fund a UTT AMIS account through Wekeza-Smart', 'Uko tayari? Fungua na weka pesa kwenye akaunti ya UTT AMIS kupitia Wekeza-Smart')}</span>
              <ChevronRight className="h-4 w-4 text-primary" />
            </Link>
          )}

          <ScrollArea className="flex-1" ref={scrollRef}>
            <div className="max-w-3xl mx-auto p-4 space-y-4">
              {messages.map((msg, i) => {
                const isUser = msg.role === 'user';
                const actions = !isUser && msg.type !== 'welcome' ? parseMessageActions(msg.content) : [];
                return (
                  <div key={i} ref={i === messages.length - 1 ? lastMsgRef : null} className={`flex ${isUser ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-2 duration-300`} data-testid={`chat-message-${msg.role}`}>
                    <div className={`flex items-end gap-2 max-w-[92%] ${isUser ? 'flex-row-reverse' : ''}`}>
                      <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${isUser ? 'bg-gradient-to-br from-primary to-primary/60' : 'bg-muted border border-border/50'}`}>
                        {isUser ? <User className="w-4 h-4 text-primary-foreground" /> : <Bot className="w-4 h-4 text-primary" />}
                      </div>
                      <div className="space-y-2 min-w-0">
                        <div className={`px-4 py-3 rounded-2xl ${isUser ? 'bg-gradient-to-br from-primary to-primary/80 text-primary-foreground rounded-br-md' : 'bg-card text-foreground border border-border/50 rounded-bl-md shadow-sm'}`}>
                          <MessageContent content={msg.content} isUser={isUser} />
                        </div>
                        {actions.length > 0 && (
                          <div className="flex flex-wrap gap-2 pl-1">
                            {actions.map((a, j) => (
                              <Button key={j} variant="outline" size="sm" onClick={a.action} className="rounded-full text-xs h-8 border-primary/30 hover:bg-primary/10" data-testid={`chat-action-${i}-${j}`}>
                                <a.icon className="w-3 h-3 mr-1.5" />{a.label}<ChevronRight className="w-3 h-3 ml-1" />
                              </Button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {isFresh && (
                <div className="grid gap-2 sm:grid-cols-3 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="chat-starters">
                  {starters.map((q, i) => (
                    <button key={q} type="button" onClick={() => sendMessage(null, q)} data-testid={`chat-starter-${i}`}
                      className="group rounded-2xl border border-border/60 bg-card p-4 text-left text-sm transition-colors duration-200 hover:border-primary/40 hover:bg-primary/5">
                      <ArrowUpRight className="mb-2 h-4 w-4 text-primary transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                      {q}
                    </button>
                  ))}
                </div>
              )}

              {loading && (
                <div className="flex justify-start" data-testid="chat-loading">
                  <div className="flex items-end gap-2">
                    <div className="w-8 h-8 rounded-xl bg-muted border border-border/50 flex items-center justify-center"><Bot className="w-4 h-4 text-primary" /></div>
                    <div className="px-4 py-3 rounded-2xl bg-card border border-border/50 rounded-bl-md flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-primary animate-pulse" />
                      <span className="text-sm text-muted-foreground">{t('Checking live market data…', 'Inakagua data ya soko…')}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          <div className="p-4 border-t border-border/50 bg-background/80 backdrop-blur-sm">
            <form onSubmit={sendMessage} className="max-w-3xl mx-auto flex items-center gap-3">
              <div className="flex-1 relative">
                <Input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} disabled={loading} data-testid="chat-input"
                  placeholder={t(`Ask about ${cat.en}: why, how, which…`, `Uliza kuhusu ${cat.sw}: kwa nini, vipi…`)}
                  className="h-12 rounded-2xl bg-muted/50 border-border/50 px-5 pr-16 focus:bg-background focus:border-primary/50" />
                <Badge variant="outline" className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] bg-background/50">GPT-5.2</Badge>
              </div>
              <Button type="submit" size="icon" className="w-12 h-12 rounded-2xl shadow-lg shadow-primary/20" disabled={loading || !input.trim()} data-testid="chat-send-btn">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
}
